import { dateKey } from '../nutrition/nutrition'

/**
 * A dated to-do list. Ticked items linger for a day so there is a visible record of what
 * got done, then disappear on their own — no archive to browse, nothing to tidy up.
 *
 * PURE — "now" is always passed in.
 */

export interface Todo {
  id: string
  title: string
  description: string
  /** YYYY-MM-DD. Ignored for daily tasks, which always belong to today. */
  date: string
  /** HH:MM, or null for "sometime that day". */
  time: string | null
  completedAt: string | null
  createdAt: string
  /** A standing task: it comes back tomorrow instead of clearing itself. */
  repeatDaily?: boolean
  /** The last day a repeating task was ticked, so today's state is unambiguous. */
  lastCompletedDate?: string | null
}

export interface TodoState {
  items: Todo[]
}

export const KEEP_COMPLETED_HOURS = 24

export function emptyTodos(): TodoState {
  return { items: [] }
}

function expiryOf(todo: Todo): number | null {
  if (todo.repeatDaily) return null
  if (!todo.completedAt) return null
  return Date.parse(todo.completedAt) + KEEP_COMPLETED_HOURS * 3_600_000
}

/** Daily tasks never expire — they are the list, not entries on it. */
export function isExpired(todo: Todo, now: Date = new Date()): boolean {
  const expiry = expiryOf(todo)
  return expiry !== null && expiry <= now.getTime()
}

/** A daily task is done only for the day it was last ticked. */
export function isDone(todo: Todo, now: Date = new Date()): boolean {
  if (todo.repeatDaily) return todo.lastCompletedDate === dateKey(now)
  return todo.completedAt !== null
}

/** Daily tasks always sit under today, whatever date they were created on. */
export function dueDate(todo: Todo, now: Date = new Date()): string {
  return todo.repeatDaily ? dateKey(now) : todo.date
}

/** Sorted for a day view: by time, then by when it was added. Untimed items go last. */
export function visibleTodos(state: TodoState, now: Date = new Date()): Todo[] {
  return state.items
    .filter((t) => !isExpired(t, now))
    .sort(
      (a, b) =>
        dueDate(a, now).localeCompare(dueDate(b, now)) ||
        (a.time ?? '99:99').localeCompare(b.time ?? '99:99') ||
        a.createdAt.localeCompare(b.createdAt)
    )
}

export function groupByDay(
  todos: Todo[],
  now: Date = new Date()
): { date: string; label: string; items: Todo[] }[] {
  const today = dateKey(now)
  const groups = new Map<string, Todo[]>()
  for (const todo of todos) {
    const key = dueDate(todo, now)
    const list = groups.get(key) ?? []
    list.push(todo)
    groups.set(key, list)
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({ date, label: dayLabel(date, today), items }))
}

function dayLabel(date: string, today: string): string {
  if (date === today) return 'Today'
  const diff = Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000)
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  if (diff < 0) return `Overdue · ${formatDay(date)}`
  return formatDay(date)
}

function formatDay(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

export function openTodosFor(state: TodoState, date: string, now: Date = new Date()): Todo[] {
  return visibleTodos(state, now).filter((t) => dueDate(t, now) === date && !isDone(t, now))
}

/** Anything not done whose day has already passed. Worth surfacing, quietly. */
export function overdueCount(state: TodoState, now: Date = new Date()): number {
  const today = dateKey(now)
  return visibleTodos(state, now).filter((t) => !isDone(t, now) && dueDate(t, now) < today).length
}
